import { Card, Form, Input as AntdInput } from 'antd';
import React, { FC, memo, useCallback } from 'react';
import { AppLogo } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { AppRoutes } from 'app/config/routerConfig/types';
import { showNotification } from 'app/providers/NotificationsProvider';
import { useSetNewPasswordMutation } from 'entities/User';
import { LangSwitcher } from 'features/LangSwitcher';
import { Button } from 'shared/ui/Button';
import { useNavigate, useParams } from 'react-router-dom';
import { ModernInputPassword } from 'shared/ui/ModernInputPassword';

export const ResetPasswordPage: FC = memo(() => {
  const { t } = useAppTranslation(['login-page', 'common', 'profile']);

  const navigate = useNavigate();

  const { resetPasswordId } = useParams();

  const [setNewPassword, { isLoading }] = useSetNewPasswordMutation();

  const [form] = Form.useForm();

  const handleSubmit = useCallback(
    async ({ newPassword }: { newPassword: string }) => {
      if (resetPasswordId) {
        try {
          await setNewPassword({ newPassword, resetPasswordId }).unwrap();
          showNotification('info', t('Success', { ns: 'common' }), t('Password reset has been successful', { ns: 'common' }));
          navigate(AppRoutes.HOME);
        } catch (error: CustomAny) {
          console.log(error);
          showNotification('error', t('Error', { ns: 'common' }), t('Error when resetting password', { ns: 'common' }));
        }
      }
    },
    [navigate, resetPasswordId, setNewPassword, t],
  );

  return (
    <div className="h-screen flex flex-col justify-center items-center bg-accentLight">
      <Card
        className="w-[370px] pt-[24px] tablet:w-[450px]"
        title={
          <div className="flex items-center p-3 justify-center">
            <AppLogo width={180} height="100%" className="w-[180px]" />
            <div className="absolute top-0 right-0">
              <LangSwitcher mobileBackgroundClassName="bg-white" />
            </div>
          </div>
        }
      >
        <Form name="resetPassword" form={form} onFinish={handleSubmit} layout="vertical">
          <div className="text-primary text-center font-semibold mb-4">
            {t('Great! Please set up a new recovery password', { ns: 'profile' })}
          </div>
          <Form.Item
            name="newPassword"
            required={false}
            label={<span className="text-grey">{t('Enter a new password', { ns: 'profile' })}</span>}
            rules={[
              { required: true, message: t('Please enter your new password!') },
              { min: 8, message: t('Password must be at least 8 characters!') },
            ]}
          >
            <ModernInputPassword placeholder={t('New password')} />
          </Form.Item>
          <Form.Item
            name="repeatPassword"
            required={false}
            label={<span className="text-grey">{t('Confirm your new password', { ns: 'profile' })}</span>}
            rules={[
              { required: true, message: t('Please repeat password!') },
              ({ getFieldValue }) => ({
                async validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    await Promise.resolve();
                    return;
                  }
                  return await Promise.reject(new Error(t('Password must be the same')));
                },
              }),
            ]}
          >
            <ModernInputPassword placeholder={t('Repeat password')} />
          </Form.Item>
          <Button type="submit" isLoading={isLoading}>
            {t('Set new password')}
          </Button>
        </Form>
      </Card>
    </div>
  );
});
